import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";
import { useBookingForm } from "./booking/useBookingForm";
import { PropertyStep } from "./booking/PropertyStep";
import { ServiceSelector } from "./booking/ServiceSelector";
import { ScheduleStep } from "./booking/ScheduleStep";
import { DetailsStep } from "./booking/DetailsStep";
import { ConfirmStep } from "./booking/ConfirmStep";
import { BookingSummary } from "./booking/BookingSummary";
import { BookingSuccess } from "./booking/BookingSuccess";

const STEPS = ["Property", "Services", "Date & Time", "Details", "Confirm"];

function stepClasses(active, complete) {
  if (active) return { text: "text-foreground font-medium", dot: "border-slate-900 bg-slate-900 text-slate-50" };
  if (complete) return { text: "text-foreground", dot: "border-slate-900" };
  return { text: "text-slate-400", dot: "border-slate-300" };
}

export default function Booking() {
  const form = useBookingForm();
  const { step, submitting, done, sqft, quote, next, back, submit } = form;

  if (done) return <BookingSuccess done={done} />;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 md:px-10 pt-32 pb-24">
        <span className="text-[11px] font-mono uppercase tracking-[0.25em] text-slate-500">New booking</span>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight mt-3">Book your shoot</h1>

        {/* Stepper */}
        <div className="flex items-center gap-2 mt-8 mb-10 flex-wrap">
          {STEPS.map((label, i) => {
            const n = i + 1;
            const active = n === step, complete = n < step;
            const classes = stepClasses(active, complete);
            return (
              <div key={label} className="flex items-center gap-2">
                <div className={`flex items-center gap-2 px-2 py-1.5 text-sm ${classes.text}`}>
                  <span className={`h-7 w-7 flex items-center justify-center text-xs border ${classes.dot}`}>
                    {complete ? <Check className="h-3 w-3" /> : n}
                  </span>
                  <span className="hidden sm:inline font-mono text-[11px] uppercase tracking-[0.1em]">{label}</span>
                </div>
                {n < STEPS.length && <div className="w-5 h-px bg-slate-300" />}
              </div>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="bg-card border border-border p-6 md:p-8 shadow-[0_2px_24px_rgba(30,41,59,0.06)]">
              {step === 1 && <PropertyStep form={form} />}
              {step === 2 && <ServiceSelector form={form} />}
              {step === 3 && <ScheduleStep form={form} />}
              {step === 4 && <DetailsStep form={form} />}
              {step === 5 && <ConfirmStep form={form} />}

              {/* Nav buttons */}
              <div className="flex items-center justify-between mt-10">
                <Button data-testid="step-back" variant="ghost" className="rounded-none" onClick={back} disabled={step === 1}>
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                {step < 5 ? (
                  <Button data-testid="step-next" className="rounded-none bg-slate-900 hover:bg-slate-800 text-slate-50 px-8" onClick={next}>
                    Continue <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button data-testid="booking-submit" className="rounded-none bg-slate-900 hover:bg-slate-800 text-slate-50 px-8" onClick={submit} disabled={submitting}>
                    {submitting ? "Submitting…" : "Submit booking"}
                  </Button>
                )}
              </div>
            </div>
          </div>

          <BookingSummary sqft={sqft} quote={quote} />
        </div>
      </div>
    </div>
  );
}
